import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import { TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { N40 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Popup, { PopupProps } from '@databases/atlaskit/Popup';
import { flaggingCallback, useFlaggingCallback } from '@databases/banners';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import { messages } from '@databases/intl/messages';
import { useComponent, useResizeObserver } from '@databases/react';
import { DatabaseFieldType } from '@databases/types/database';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import composeRefs from '@seznam/compose-react-refs';
import React, {
  FunctionComponent,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { unreachable } from '../../../common/utils';
import { FIELD_TYPES, FieldTypeCategory } from '../field-types/fieldTypes';

import FieldTypeIcon from './FieldTypeIcon';
import FieldTypeSearch from './FieldTypeSearch';

const EmptyItem = styled(ButtonItem)`
  pointer-events: none;
  color: ${colors.subtleText} !important;
`;

const StyledButton = styled(Button)`
  && {
    text-align: left;
    align-items: center;
    font-weight: normal;
    padding: 4px 8px;
    border-radius: 3px;
    ${({ shouldFitContainer }) => shouldFitContainer && `height: 40px`};
    ${({ isSelected }) =>
      isSelected
        ? 'border: none'
        : `border: ${token('space.025', '2px')} solid ${token(
            'color.border.input',
            N40
          )}`};
    ${({ isInvalid }) => isInvalid && `border-color: ${token('color.border.danger')}`};
  }
`;

const PopupContentContainer = styled.div``;

export interface FieldTypeTriggerProps extends TriggerProps {
  onFocus?: (e?: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  isSelected?: boolean;
}

export type FieldTypeDropdownProps = Omit<
  PopupProps,
  'trigger' | 'isOpen' | 'content'
> & {
  value?: DatabaseFieldType;
  onChange: (value: DatabaseFieldType) => void;
  onFocus?: (e?: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
  trigger?: string | ((triggerButtonProps: FieldTypeTriggerProps) => ReactElement);
  shouldFitContainer?: boolean;
  isDisabled?: boolean;
  hasFieldTypeError?: boolean;

  /** If specified, only these types are selectable. Otherwise all existing types are selectable. */
  showTypes?: DatabaseFieldType[];
  maxHeight?: string;
};

/**
 * Renders a dropdown menu that allows selecting a database field type.
 */
const FieldTypeDropdown: FunctionComponent<FieldTypeDropdownProps> = ({
  maxHeight = '435px',
  value,
  onChange,
  trigger,
  onFocus,
  onBlur,
  shouldFitContainer,
  showTypes,
  isDisabled,
  hasFieldTypeError,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { formatMessage } = useIntl();

  const [isOpen, setIsOpen] = useState(false);
  const handleOpenChange = useFlaggingCallback(() => {
    setIsOpen((isOpen) => {
      if (isOpen) {
        setSearchValue('');
      }
      return !isOpen;
    });
  }, []);
  const [outerTrigger, setOuterTrigger] = useState<HTMLElement | null>(null);
  const menuWidth = useResizeObserver(
    shouldFitContainer ? outerTrigger : undefined
  )?.width;
  const isCheckboxEnabled = useBooleanFeatureFlag(
    'databases-frontend-checkbox-field-type'
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchValue('');
  }, [setIsOpen, setSearchValue]);

  useHandleOutsideClick(
    handleClose,
    dropdownRef,
    ['[id="field-type-dropdown-trigger"]'],
    isOpen
  );

  const triggerComp = useComponent(
    ({ ref, ...props }: TriggerProps) =>
      trigger && typeof trigger === 'function' ? (
        trigger({
          ref: composeRefs((r) => setOuterTrigger(r), ref),
          onFocus,
          onBlur,
          onClick: handleOpenChange,
          isSelected: props['aria-expanded'],
          ...props,
        })
      ) : typeof trigger === 'string' ? (
        <span ref={ref} onClick={handleOpenChange} {...props}>
          {trigger}
        </span>
      ) : (
        <StyledButton
          id="field-type-dropdown-trigger"
          testId="field-type-dropdown-trigger"
          iconBefore={value ? <FieldTypeIcon type={value} noTooltip /> : undefined}
          iconAfter={<ChevronDownIcon size="medium" label="" />}
          ref={composeRefs((r) => setOuterTrigger(r), ref)}
          onFocus={onFocus}
          onBlur={onBlur}
          shouldFitContainer={shouldFitContainer}
          onClick={handleOpenChange}
          isSelected={props['aria-expanded']}
          isDisabled={isDisabled}
          appearance="subtle"
          isInvalid={hasFieldTypeError}
          {...props}
        >
          {trigger ?? (value && formatMessage(FIELD_TYPES[value].label))}
        </StyledButton>
      ),
    [
      formatMessage,
      handleOpenChange,
      hasFieldTypeError,
      isDisabled,
      onBlur,
      onFocus,
      shouldFitContainer,
      trigger,
      value,
    ]
  );

  const getCategoryName = (cat: FieldTypeCategory) => {
    switch (cat) {
      case FieldTypeCategory.BASIC:
        return messages['field-type.category-basic'];
      case FieldTypeCategory.PAGE:
        return messages['field-type.category-page'];
      case FieldTypeCategory.JIRA:
        return messages['field-type.category-jira'];
      case FieldTypeCategory.OTHER_DATABASES:
        return messages['field-type.category-other-databases'];
      default:
        return unreachable(cat);
    }
  };

  const items = useMemo(
    () =>
      Object.values(FieldTypeCategory)
        .map(
          (cat) =>
            [
              formatMessage(getCategoryName(cat)),
              Object.entries(FIELD_TYPES).filter(([type, data]) => {
                if (!isCheckboxEnabled && type === 'checkbox') {
                  return;
                }
                return (
                  data.category === cat &&
                  (!showTypes || showTypes.includes(type as DatabaseFieldType)) &&
                  formatMessage(data.label)
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
                );
              }),
            ] as const
        )
        .filter(([_, types]) => types.length > 0),
    [formatMessage, isCheckboxEnabled, searchValue, showTypes]
  );

  return (
    <Popup
      isOpen={isOpen}
      onClose={handleOpenChange}
      contentContainer={PopupContentContainer}
      trigger={triggerComp}
      content={({ setInitialFocusRef }) => (
        <Box ref={dropdownRef}>
          <MenuGroup
            testId="field-type-dropdown"
            minWidth={menuWidth ? menuWidth : undefined}
            maxHeight={maxHeight}
            onClick={(e) => e.stopPropagation()}
          >
            <FieldTypeSearch
              onChange={setSearchValue}
              value={searchValue}
              ref={setInitialFocusRef}
            />
            <Box paddingBlockStart="space.400">
              {items.map(([cat, types]) => (
                <Section
                  key={cat}
                  title={items.length > 1 ? cat : undefined}
                  hasSeparator
                >
                  {types.map(([type, { label, shortDescription }]) => (
                    <Tooltip
                      key={type}
                      content={shortDescription && formatMessage(shortDescription)}
                      hideTooltipOnMouseDown
                      position="left"
                    >
                      <ButtonItem
                        data-testid={`field-type-dropdown-${type}`}
                        iconBefore={
                          <FieldTypeIcon type={type as DatabaseFieldType} noTooltip />
                        }
                        onClick={flaggingCallback(() => {
                          onChange(type as DatabaseFieldType);
                          handleOpenChange();
                        })}
                        isSelected={type === value}
                      >
                        {formatMessage(label)}
                      </ButtonItem>
                    </Tooltip>
                  ))}
                </Section>
              ))}
            </Box>

            {!items.length && (
              <Section hasSeparator>
                <EmptyItem>
                  {formatMessage(
                    messages['field-settings.field-type-dropdown-no-matches-text']
                  )}
                </EmptyItem>
              </Section>
            )}
          </MenuGroup>
        </Box>
      )}
      {...props}
    />
  );
};

export default FieldTypeDropdown;
